import Head from 'next/head';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@zillow/constellation-10';
import { useSatelliteWebRenterConversationHandler_updateStatus } from '@zg-rentals/ts-satellite-client-react-query';
import {
  MultiBrandSupportUserReportConversationReasonType,
  SatelliteConversationFolderTypeV2,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import Layout from '../../components/Layout';
import { AppContext } from '../../contexts/AppContextProvider';
import { withAuthBoundary } from '../../AuthProvider';
import Header from '../../components/Header';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import getInboxPageHeadTitle from '../../utils/getInboxPageHeadTitle';
import { InboxPageContextProvider } from '../../contexts/InboxPageContextProvider';
import { getValueFromEnum } from '../../utils/getValueFromEnum';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import useInboxNextRouter from '../../hooks/useInboxNextRouter';
import {
  columnWrapperStyles,
  inboxMainContentStyles,
  inboxPageWrapperStyles,
  pageContentStyles,
  primaryColumnStyles,
  secondaryColumnStyles,
} from '../../styles/inboxPageStyle';
import { emitInboxPageViewEvent, INBOX_PAGE_VIEW_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/pageView';
import { MessageListSkeleton } from '../../components/MessageList/skeleton';
import ConversationNavSkeleton from '../../components/ConversationNav/Skeleton';
import ConversationList, { getConversationToRedirectToNext } from '../../components/ConversationList';
import type { ConversationListRef } from '../../components/ConversationList';
import NoConversationsContent from '../../components/NoConversationsContent';
import FullPageNoConversationsWrapper from '../../components/FullPageNoConversationsWrapper';
import WebSocketStatus from '../../components/WebSocketStatus';
import type { UpdateConversationStateArgs } from '../../types/UpdateConversationStateArgs';
import ConversationNav, { ConversationActionTypes } from '../../components/ConversationNav';
import MessageList from '../../components/MessageList';
import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useInfiniteConversationList } from '../../hooks/useInfiniteConversationList';
import { useWebSocketContext } from '@zg-rentals/web-socket-react';
import ConfigConstants from '../../constants/ConfigConstants';
import type { User } from '../../AuthProvider';
import type { Brand } from '@zg-rentals/multibrand-support';

const INBOX_POLL_INTERVAL_MS = 60 * 1000;

const getPostDataByAction = (
  action: ConversationActionTypes,
  conversationId: string,
): {
  conversationId: string;
  isArchived?: boolean;
  reportReasonType?: MultiBrandSupportUserReportConversationReasonType;
} => {
  switch (action) {
    case ConversationActionTypes.Archive:
      return { conversationId, isArchived: true };
    case ConversationActionTypes.Report:
      return { conversationId, reportReasonType: MultiBrandSupportUserReportConversationReasonType.SPAM };
    case ConversationActionTypes.MoveToInbox:
      return {
        conversationId,
        isArchived: false,
        reportReasonType: MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED,
      };
    default:
      logError({ error: `No case for: ${action}`, errorType: 'GetVariablesByAction', context: { action } });
      return { conversationId };
  }
};

export default withAuthBoundary(function InboxPage({ user, brand: brandFromProps }: { user: User; brand: Brand }) {
  const pageHeadTitle = getInboxPageHeadTitle(brandFromProps);
  const router = useRouter();
  const { query } = router;
  const conversationId = query.conversationId?.[0];
  const queryClient = useQueryClient();
  const conversationListRef = useRef<ConversationListRef | null>(null);
  const { folder: folderFromQuery, mobileView } = query;
  const conversationIdValue = conversationId && String(conversationId);
  const folder = getFolderFromQueryParam(folderFromQuery?.toString() || '');
  const [selectedFolder, setSelectedFolder] = useState<SatelliteConversationFolderTypeV2>(folder);
  const { isMobileDevice } = useContext(AppContext);
  const { changeRoute } = useInboxNextRouter();
  const [shouldOpenAttachmentsUploader, setShouldOpenAttachmentsUploader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldFetchOlderConversations, setShouldFetchOlderConversations] = useState(false);
  const [conversationListLoadedWithCount, setConversationListLoadedWithCount] = useState<number | null>(null);
  const [conversationListError, setConversationListError] = useState<Error | null>(null);
  const hasRedirectedToFirstConversationRef = useRef(false);
  /** Set by AddMessage when user sends a message; ConversationList and MessageList skip refetching getMessages when this is set to avoid duplicate API calls. */
  const messageJustSentRef = useRef<{ conversationId: string; messageId: string; at: number } | null>(null);
  /** True after handleFolderChange until the next refetch is triggered; ensures we refetch even when ConversationList unmounts/remounts (and loses hook state). */
  const folderSwitchRefetchPendingRef = useRef(false);
  /** When count is null we're in folder-switch loading; only the page-level settled effect should set count (so we use refetch result, not stale cache). */
  const conversationListLoadedWithCountRef = useRef<number | null>(conversationListLoadedWithCount);
  conversationListLoadedWithCountRef.current = conversationListLoadedWithCount;

  const { isWebSocketEnabledAndReady } = useWebSocketContext();

  const {
    conversations: pageListConversations,
    isLoading: pageListIsLoading,
    isRefetching: pageListIsRefetching,
    error: pageListError,
  } = useInfiniteConversationList({
    folder: selectedFolder,
    daysBack: shouldFetchOlderConversations ? ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS : null,
    isWebSocketEnabledAndReady,
    pollIntervalMs: INBOX_POLL_INTERVAL_MS,
  });

  const { mutateAsync: updateConversationStatus } = useSatelliteWebRenterConversationHandler_updateStatus({
    meta: {
      context: {
        api: 'updateStatus',
        details: 'InboxPage - Error updating conversation status for undo',
      },
    },
  });

  const handleFolderChange = (selectedOption: SatelliteConversationFolderTypeV2) => {
    setSelectedFolder(selectedOption);
    hasRedirectedToFirstConversationRef.current = false;
    setIsLoading(true);
    setConversationListLoadedWithCount(null);
    setConversationListError(null);
    folderSwitchRefetchPendingRef.current = true;

    // Invalidate the new folder's conversation list so it refetches once when the hook observes it.
    // Single trigger here avoids duplicate getLatestConversations (hook folder-effect + query becoming active).
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === SatelliteWebRenterConversationHandler_getLatestConversations_Path &&
        (query.queryKey[1] as { folder?: string })?.folder === selectedOption,
    });

    // Clear conversation so we show two-column layout with skeletons in both columns; once list loads we redirect to first conversation or show NoConversationsContent.
    changeRoute({
      selectedOption: selectedOption,
      conversationIdToRedirectTo: '',
      isMobile: isMobileDevice,
      mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
    });
  };

  const enumValuesFromQueryParams = {
    folder: selectedFolder,
    mobileView: getValueFromEnum(mobileView?.toString(), InboxPageMobileView) || InboxPageMobileView.ConversationList,
  };

  const handleConversationsLoaded = useCallback(
    (conversations: Array<{ conversationId?: string }>, listIsLoading: boolean, listError: Error | null) => {
      if (listIsLoading) return;
      setIsLoading(false);
      // When in folder-switch loading (count is null), don't set count here — the page-level "settled" effect will set it from the refetched query so we use refetch result, not stale cache. Avoids applying cached (0, false) before refetch completes.
      if (conversationListLoadedWithCountRef.current === null) {
        return;
      }
      setConversationListLoadedWithCount(conversations.length);
      setConversationListError(listError);

      if (listError) return;

      // Only redirect to first conversation when we have no conversationId (e.g. user opened /inbox).
      // When we have a conversationId (e.g. from /inbox/inquiry/{inquiryId} redirect), keep it.
      if (conversations.length > 0 && !conversationId && !hasRedirectedToFirstConversationRef.current) {
        hasRedirectedToFirstConversationRef.current = true;
        const firstConversationId = conversations[0]?.conversationId ?? '';
        changeRoute({
          selectedOption: selectedFolder,
          conversationIdToRedirectTo: firstConversationId,
          isMobile: isMobileDevice,
          mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
        });
      } else if (conversations.length === 0) {
        // List is empty (e.g. last conversation was moved out). Clear conversationId so we show no-conversation UI in both columns instead of keeping the moved conversation in the right column.
        changeRoute({
          selectedOption: selectedFolder,
          conversationIdToRedirectTo: '',
          isMobile: isMobileDevice,
          mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
        });
      }
    },
    [selectedFolder, isMobileDevice, enumValuesFromQueryParams.mobileView, changeRoute, conversationId],
  );

  useEffect(() => {
    if (conversationId) {
      setIsLoading(false);
    }
  }, [conversationId]);

  // Clear folder-switch ref so we don't treat the next load as a duplicate trigger. The actual refetch
  // is done by useInfiniteConversationList when folder (selectedFolder) changes.
  useEffect(() => {
    if ((isLoading || conversationListLoadedWithCount === null) && folderSwitchRefetchPendingRef.current) {
      folderSwitchRefetchPendingRef.current = false;
    }
  }, [isLoading, conversationListLoadedWithCount, selectedFolder]);

  // When "Load older" was clicked from no-conversation UI: we have count 0 but page-level list (with daysBack 660) may have just loaded. ConversationList is not mounted in that branch so it never calls onConversationsLoaded. Update count from page list so we switch to two-column layout.
  useEffect(() => {
    if (
      conversationListLoadedWithCount !== 0 ||
      !shouldFetchOlderConversations ||
      pageListIsLoading ||
      pageListIsRefetching ||
      pageListConversations.length === 0
    )
      return;
    const count = pageListConversations.length;
    setConversationListLoadedWithCount(count);
    setConversationListError(pageListError ?? null);
    if (count > 0 && !hasRedirectedToFirstConversationRef.current) {
      hasRedirectedToFirstConversationRef.current = true;
      const firstId = pageListConversations[0]?.conversationId ?? '';
      changeRoute({
        selectedOption: selectedFolder,
        conversationIdToRedirectTo: firstId,
        isMobile: isMobileDevice,
        mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
      });
    }
  }, [
    conversationListLoadedWithCount,
    shouldFetchOlderConversations,
    pageListIsLoading,
    pageListIsRefetching,
    pageListConversations,
    pageListError,
    selectedFolder,
    isMobileDevice,
    enumValuesFromQueryParams.mobileView,
    changeRoute,
  ]);

  // When in folder-switch loading (count is null), set count from the page-level query once it has settled after refetch so we use refetch result instead of stale cache. Fixes: (1) Archived shows no-conversation after archiving from Recent, (2) empty folder stuck in loading instead of no-conversation UI.
  useEffect(() => {
    if (conversationListLoadedWithCount !== null || pageListIsLoading || pageListIsRefetching) return;
    const count = pageListConversations.length;
    setConversationListLoadedWithCount(count);
    setConversationListError(pageListError ?? null);
    // Only redirect to first when we have no conversationId (e.g. folder switch cleared it); keep existing conversationId when present (e.g. from inquiry deep link).
    if (count > 0 && !conversationId && !hasRedirectedToFirstConversationRef.current) {
      hasRedirectedToFirstConversationRef.current = true;
      const firstId = pageListConversations[0]?.conversationId ?? '';
      changeRoute({
        selectedOption: selectedFolder,
        conversationIdToRedirectTo: firstId,
        isMobile: isMobileDevice,
        mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
      });
    } else if (count === 0) {
      changeRoute({
        selectedOption: selectedFolder,
        conversationIdToRedirectTo: '',
        isMobile: isMobileDevice,
        mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
      });
    }
  }, [
    conversationListLoadedWithCount,
    pageListIsLoading,
    pageListIsRefetching,
    pageListConversations,
    pageListError,
    selectedFolder,
    isMobileDevice,
    enumValuesFromQueryParams.mobileView,
    changeRoute,
    conversationId,
  ]);

  // When navigating to inbox without a conversationId, redirect to first conversation if list is already loaded
  useEffect(() => {
    if (conversationId || isLoading) return;
    const conversations = conversationListRef.current?.getConversations() ?? [];
    if (conversations.length > 0 && !hasRedirectedToFirstConversationRef.current) {
      hasRedirectedToFirstConversationRef.current = true;
      const firstConversationId = conversations[0]?.conversationId ?? '';
      changeRoute({
        selectedOption: selectedFolder,
        conversationIdToRedirectTo: firstConversationId,
        isMobile: isMobileDevice,
        mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
      });
    }
  }, [conversationId, isLoading, selectedFolder, isMobileDevice, enumValuesFromQueryParams.mobileView, changeRoute]);

  // Reset attachments uploader state when conversation changes
  useEffect(() => {
    setShouldOpenAttachmentsUploader(false);
  }, [conversationId]);

  const handleSecondaryColumnDragEnter = () => setShouldOpenAttachmentsUploader(true);
  const handleSecondaryColumnDragLeave = () => setShouldOpenAttachmentsUploader(false);

  useEffect(() => {
    setShouldOpenAttachmentsUploader(false);
    window.addEventListener('dragenter', handleSecondaryColumnDragEnter);
    window.addEventListener('dragleave', handleSecondaryColumnDragLeave);

    return () => {
      window.removeEventListener('dragenter', handleSecondaryColumnDragEnter);
      window.removeEventListener('dragleave', handleSecondaryColumnDragLeave);
    };
  }, []);

  useEffect(() => {
    emitInboxPageViewEvent();
  }, []);

  const updateConversationStatusByAction = async (conversationId: string, action: ConversationActionTypes) => {
    try {
      await updateConversationStatus({
        postData: getPostDataByAction(action, conversationId),
      });
    } catch (err) {
      logError({ error: err, context: { conversationId, info: `Error on ${action} conversation` } });
    }
  };

  const showMessageListOnMobile = () => {
    changeRoute({
      conversationIdToRedirectTo: conversationId,
      mobileViewToChangeTo: InboxPageMobileView.MessageList,
      isMobile: isMobileDevice,
    });
  };

  const showConversationListOnMobile = () => {
    changeRoute({
      conversationIdToRedirectTo: conversationId,
      mobileViewToChangeTo: InboxPageMobileView.ConversationList,
      isMobile: isMobileDevice,
    });
  };

  const handleUndoClick = async (conversationIdToUndo: string) => {
    if (!conversationIdToUndo) return;

    const updateConversationStatus = () => {
      switch (selectedFolder) {
        case SatelliteConversationFolderTypeV2.ARCHIVED:
          return updateConversationStatusByAction(conversationIdToUndo, ConversationActionTypes.Archive);
        case SatelliteConversationFolderTypeV2.SPAM:
          return updateConversationStatusByAction(conversationIdToUndo, ConversationActionTypes.Report);
        case SatelliteConversationFolderTypeV2.INBOX:
          return updateConversationStatusByAction(conversationIdToUndo, ConversationActionTypes.MoveToInbox);
        default:
          logError({
            error: `No case for undo click: ${selectedFolder}`,
            errorType: 'StatusUpdateError',
            context: { conversationIdToUndo },
          });
      }
    };

    await updateConversationStatus();

    changeRoute({
      selectedOption: selectedFolder,
      conversationIdToRedirectTo: conversationIdToUndo,
      isMobile: isMobileDevice,
    });
    refetchConversationList({ conversationIdToRedirectTo: conversationIdToUndo });
  };

  const refetchConversationList = async ({ conversationIdToRedirectTo }: { conversationIdToRedirectTo?: string }) => {
    try {
      // Trigger refetch first to get updated list
      await conversationListRef.current?.refetch();

      // Now get the fresh conversations after refetch completes
      const freshConversations = conversationListRef.current?.getConversations() || [];

      const nextConversationId =
        conversationIdToRedirectTo || getConversationToRedirectToNext(conversationId!, freshConversations);

      if (nextConversationId) {
        updateConversationState({ conversationId: nextConversationId });
      }
    } catch (err) {
      logError({ error: err, context: 'Error refetching conversations' });
    }
  };

  const updateConversationState = ({
    conversationId,
    hasLoadedOlderConversations,
    showConversationList = true,
    shouldChangeRoute = true,
  }: UpdateConversationStateArgs): void => {
    if (hasLoadedOlderConversations) {
      setShouldFetchOlderConversations(hasLoadedOlderConversations);
    }

    if (shouldChangeRoute) {
      changeRoute({
        conversationIdToRedirectTo: conversationId,
        mobileViewToChangeTo: showConversationList
          ? InboxPageMobileView.ConversationList
          : InboxPageMobileView.MessageList,
        isMobile: isMobileDevice,
      });
    }
  };

  const shouldHideConversationListOnMobile =
    enumValuesFromQueryParams.mobileView !== InboxPageMobileView.ConversationList;
  const shouldShowNoConversationsUI =
    !conversationListError &&
    !isLoading &&
    conversationListLoadedWithCount !== null &&
    conversationListLoadedWithCount === 0;

  const conversationListProps = {
    ref: conversationListRef,
    showMessageListOnMobile,
    updateConversationState,
    shouldFetchOlderConversations,
    onConversationsLoaded: handleConversationsLoaded,
  };

  if (conversationListError && !conversationId) {
    return (
      <Layout user={user} mainContentStyles={inboxMainContentStyles} pageWrapperStyles={inboxPageWrapperStyles}>
        <>
          {/* oxlint-disable-next-line next/no-duplicate-head -- next/head (not next/document); two mutually-exclusive early-return branches, never rendered together */}
          <Head>
            <title>{pageHeadTitle}</title>
          </Head>
          <Box css={pageContentStyles} data-eventid={INBOX_PAGE_VIEW_EVENT_TYPE_ID} data-eventdescription="Page view">
            <InboxPageContextProvider
              value={{
                conversationId: conversationIdValue,
                folder: enumValuesFromQueryParams.folder,
                mobileView: enumValuesFromQueryParams.mobileView,
                messageJustSentRef,
              }}
            >
              <WebSocketStatus />
              <Header onFolderChange={handleFolderChange} />
              <FullPageNoConversationsWrapper>
                <NoConversationsContent user={user} showErrorExperience={true} />
              </FullPageNoConversationsWrapper>
            </InboxPageContextProvider>
          </Box>
        </>
      </Layout>
    );
  }

  return (
    <Layout user={user} mainContentStyles={inboxMainContentStyles} pageWrapperStyles={inboxPageWrapperStyles}>
      <>
        <Head>
          <title>{pageHeadTitle}</title>
        </Head>
        <Box css={pageContentStyles} data-eventid={INBOX_PAGE_VIEW_EVENT_TYPE_ID} data-eventdescription="Page view">
          <InboxPageContextProvider
            value={{
              conversationId: conversationIdValue,
              folder: enumValuesFromQueryParams.folder,
              mobileView: enumValuesFromQueryParams.mobileView,
              messageJustSentRef,
            }}
          >
            <WebSocketStatus />

            <Header onFolderChange={handleFolderChange} />

            {/* Loading (e.g. after folder change): always show two-column skeleton so the right column never shows the previous folder's conversation. Router may still have old conversationId for a moment. */}
            {(isLoading || conversationListLoadedWithCount === null) && (
              <Box css={columnWrapperStyles}>
                <Box
                  css={{
                    ...primaryColumnStyles,
                    display: shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
                  }}
                >
                  <ConversationList {...conversationListProps} />
                </Box>
                <Box
                  css={{
                    ...secondaryColumnStyles,
                    display: !shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
                  }}
                >
                  <Box
                    css={{
                      borderBottom: '1px solid',
                      borderBottomColor: 'border.subtle',
                      paddingBlock: 'default',
                    }}
                  >
                    <ConversationNavSkeleton />
                  </Box>
                  <Box css={{ padding: 'default' }}>
                    <MessageListSkeleton />
                  </Box>
                </Box>
              </Box>
            )}

            {shouldShowNoConversationsUI && (
              <FullPageNoConversationsWrapper>
                <NoConversationsContent
                  user={user}
                  updateConversationState={updateConversationState}
                  showErrorExperience={false}
                  hasLoadedOlderWithZeroResults={shouldFetchOlderConversations && conversationListLoadedWithCount === 0}
                />
              </FullPageNoConversationsWrapper>
            )}

            {/* No conversationId but list has conversations: show two-column with list + right skeleton until redirect to first conversation updates the URL. */}
            {!conversationId &&
              !shouldShowNoConversationsUI &&
              !(isLoading || conversationListLoadedWithCount === null) && (
                <Box css={columnWrapperStyles}>
                  <Box
                    css={{
                      ...primaryColumnStyles,
                      display: shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
                    }}
                  >
                    <ConversationList {...conversationListProps} />
                  </Box>
                  <Box
                    css={{
                      ...secondaryColumnStyles,
                      display: !shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
                    }}
                  >
                    <Box
                      css={{
                        borderBottom: '1px solid',
                        borderBottomColor: 'border.subtle',
                        paddingBlock: 'default',
                      }}
                    >
                      <ConversationNavSkeleton />
                    </Box>
                    <Box css={{ padding: 'default' }}>
                      <MessageListSkeleton />
                    </Box>
                  </Box>
                </Box>
              )}

            {/* Conversation selected and not loading: two-column layout with list + message view. URL and right column stay in sync. Don't show when list is empty (e.g. last conversation moved) so we only show no-conversation UI. */}
            {conversationId &&
              !(isLoading || conversationListLoadedWithCount === null) &&
              !shouldShowNoConversationsUI && (
                <Box css={columnWrapperStyles}>
                  <Box
                    css={{
                      ...primaryColumnStyles,
                      display: shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
                    }}
                  >
                    <ConversationList {...conversationListProps} />
                  </Box>
                  <Box
                    css={{
                      ...secondaryColumnStyles,
                      display: !shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
                    }}
                    onDragEnter={handleSecondaryColumnDragEnter}
                    onDragLeave={handleSecondaryColumnDragLeave}
                  >
                    <ConversationNav
                      user={user}
                      refetchConversationList={refetchConversationList}
                      showConversationListOnMobile={showConversationListOnMobile}
                      onToastUndoClick={handleUndoClick}
                    />
                    <MessageList
                      user={user}
                      shouldAllowSendingAttachmentsOnly
                      shouldOpenAttachmentsUploader={shouldOpenAttachmentsUploader}
                      shouldFetchOlderConversations={shouldFetchOlderConversations}
                    />
                  </Box>
                </Box>
              )}
          </InboxPageContextProvider>
        </Box>
      </>
    </Layout>
  );
});
